import { useState, useEffect } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'
import MapSelector from './MapSelector'
import LogbookTab from './LogbookTab'

interface InfrastructureObject {
  id: number
  client_id: number
  client_name: string
  name: string
  infrastructure_type?: string
  address: string
  description: string
  location: string
  street_address: string
  latitude: string
  longitude: string
  logbook_enabled?: boolean
  created_at: string
}

interface Requirement {
  id: string
  subject: string
  requirement_type: 'ZM' | 'ZU' | 'ZN'
  priority: 'low' | 'medium' | 'high'
  created_at: string
  status: string
}

interface Report {
  id: string
  work_type: 'konserwacja' | 'awaria'
  category?: 'water' | 'sewer' | null
  date: string
  location: string
  street_address: string
  is_finished: boolean
  created_at: string
}

interface Issue {
  id: string
  issue_type: string
  status: string
  reporter_name: string
  location: string
  street_address: string
  created_at: string
}

interface InfrastructureDetailsModalProps {
  infrastructureObject: InfrastructureObject | null
  isOpen: boolean
  onClose: () => void
  token: string
}

export default function InfrastructureDetailsModal({
  infrastructureObject,
  isOpen,
  onClose,
  token
}: InfrastructureDetailsModalProps) {
  const [activeTab, setActiveTab] = useState<'general' | 'reports' | 'issues' | 'requirements' | 'notes' | 'logbook'>('general')
  const [requirements, setRequirements] = useState<Requirement[]>([])
  const [reports, setReports] = useState<Report[]>([])
  const [issues, setIssues] = useState<Issue[]>([])
  const [notes, setNotes] = useState('')
  const [savingNotes, setSavingNotes] = useState(false)
  const [loadingRequirements, setLoadingRequirements] = useState(false)
  const [loadingReports, setLoadingReports] = useState(false)
  const [loadingIssues, setLoadingIssues] = useState(false)
  const [user, setUser] = useState<{role: string} | null>(null)
  const { showSuccess, showError } = useNotificationContext()

  const isAdmin = user?.role === 'ADMIN'

  useEffect(() => {
    loadUserData()
    if (isOpen && infrastructureObject) {
      loadRequirements()
      loadNotes()
      setActiveTab('general')
    }
  }, [isOpen, infrastructureObject])

  const loadRequirements = async () => {
    if (!infrastructureObject) return

    try {
      setLoadingRequirements(true)
      const response = await fetch(`/api/infrastructure/${infrastructureObject.id}/requirements`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.ok) {
        const data = await response.json()
        setRequirements(data)
      } else {
        showError('Błąd', 'Nie można pobrać listy wymagań')
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    } finally {
      setLoadingRequirements(false)
    }
  }

  const loadReports = async () => {
    if (!infrastructureObject) return

    try {
      setLoadingReports(true)
      const response = await fetch(`/api/infrastructure/${infrastructureObject.id}/reports`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.ok) {
        const data = await response.json()
        setReports(data)
      } else {
        showError('Błąd', 'Nie można pobrać listy raportów')
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    } finally {
      setLoadingReports(false)
    }
  }

  const loadIssues = async () => {
    if (!infrastructureObject) return

    try {
      setLoadingIssues(true)
      const response = await fetch(`/api/infrastructure/${infrastructureObject.id}/issues`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.ok) {
        const data = await response.json()
        setIssues(data)
      } else {
        showError('Błąd', 'Nie można pobrać listy zgłoszeń')
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    } finally {
      setLoadingIssues(false)
    }
  }

  const loadNotes = async () => {
    if (!infrastructureObject) return

    try {
      const response = await fetch(`/api/infrastructure/${infrastructureObject.id}/notes`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.ok) {
        const data = await response.json()
        setNotes(data.notes || '')
      } else {
        setNotes('')
      }
    } catch {
      setNotes('')
    }
  }

  const saveNotes = async () => {
    if (!infrastructureObject) return

    try {
      setSavingNotes(true)
      const response = await fetch(`/api/infrastructure/${infrastructureObject.id}/notes`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes }),
      })

      if (response.ok) {
        showSuccess('Zapisano', 'Notatki zostały zapisane pomyślnie')
      } else {
        showError('Błąd', 'Nie można zapisać notatek')
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    } finally {
      setSavingNotes(false)
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50'
      case 'medium': return 'text-yellow-600 bg-yellow-50'
      case 'low': return 'text-green-600 bg-green-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'high': return 'Wysoki'
      case 'medium': return 'Średni'
      case 'low': return 'Niski'
      default: return 'Nieznany'
    }
  }

  const loadUserData = async () => {
    try {
      const response = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.ok) {
        const userData = await response.json()
        setUser({ role: userData.role })
      } else {
        onClose()
      }
    } catch {
      onClose()
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'ZM': return 'Materiał'
      case 'ZU': return 'Usługa'
      case 'ZN': return 'Narzędzia'
      default: return 'Nieznany'
    }
  }

  if (!isOpen || !infrastructureObject) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Szczegóły obiektu: {infrastructureObject.name}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
            aria-label="Zamknij"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b bg-gray-50">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-6 py-3 text-sm font-medium ${
              activeTab === 'general'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            Informacje ogólne
          </button>
          <button
            onClick={() => {
              setActiveTab('reports')
              loadReports()
            }}
            className={`px-6 py-3 text-sm font-medium ${
              activeTab === 'reports'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            Raporty ({reports.length})
          </button>
          <button
            onClick={() => {
              setActiveTab('issues')
              loadIssues()
            }}
            className={`px-6 py-3 text-sm font-medium ${
              activeTab === 'issues'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            Zgłoszenia ({issues.length})
          </button>
          <button
            onClick={() => setActiveTab('requirements')}
            className={`px-6 py-3 text-sm font-medium ${
              activeTab === 'requirements'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            Zapotrzebowania ({requirements.length})
          </button>
          <button
            onClick={() => setActiveTab('notes')}
            className={`px-6 py-3 text-sm font-medium ${
              activeTab === 'notes'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            Notatki
          </button>
          <button
            onClick={() => setActiveTab('logbook')}
            className={`px-6 py-3 text-sm font-medium ${
              activeTab === 'logbook'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            📔 Dziennik
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)]">
          {activeTab === 'logbook' && infrastructureObject && (
            <div className="p-6">
              <LogbookTab
                objectId={infrastructureObject.id}
                objectName={infrastructureObject.name}
                token={token}
                userRole={user?.role}
              />
            </div>
          )}
          {activeTab === 'general' && (
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nazwa obiektu
                    </label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {infrastructureObject.name}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Klient
                    </label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {infrastructureObject.client_name}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Miejscowość
                    </label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {infrastructureObject.location || '-'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ulica/nr działki
                    </label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {infrastructureObject.street_address || '-'}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Współrzędne geograficzne
                    </label>
                    <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      <div>Szerokość: {infrastructureObject.latitude || '-'}</div>
                      <div>Długość: {infrastructureObject.longitude || '-'}</div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data utworzenia
                    </label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {new Date(infrastructureObject.created_at).toLocaleDateString('pl-PL')}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adres
                </label>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded min-h-[60px]">
                  {infrastructureObject.address || 'Brak adresu'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Opis
                </label>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded min-h-[60px]">
                  {infrastructureObject.description || 'Brak opisu'}
                </p>
              </div>

              {(infrastructureObject.latitude && infrastructureObject.longitude) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lokalizacja na mapie
                  </label>
                  <div className="border rounded-lg overflow-hidden">
                    <MapSelector
                      latitude={infrastructureObject.latitude}
                      longitude={infrastructureObject.longitude}
                      onLocationChange={() => {}}
                      height="300px"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="p-6">
              {loadingReports ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2">Ładowanie raportów...</span>
                </div>
              ) : reports.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-4xl mb-4">📊</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Brak raportów
                  </h3>
                  <p className="text-sm">
                    Nie ma jeszcze raportów przypisanych do tego obiektu infrastruktury.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reports.map((report) => (
                    <div key={report.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-sm font-medium text-gray-900">
                              {report.id}
                            </h4>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              report.work_type === 'awaria' ? 'text-red-600 bg-red-50' : 'text-blue-600 bg-blue-50'
                            }`}>
                              {report.work_type === 'awaria' ? 'Awaria' : 'Konserwacja'}
                            </span>
                            <span className="px-2 py-1 text-xs font-medium rounded-full text-gray-700 bg-gray-100">
                              {report.category === 'water'
                                ? 'Wodociągi'
                                : report.category === 'sewer'
                                  ? 'Kanalizacja'
                                  : 'Brak kategorii'}
                            </span>
                            {report.is_finished && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full text-green-600 bg-green-50">
                                Zakończony
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-600">
                            <span>Data: {new Date(report.date).toLocaleDateString('pl-PL')}</span>
                            <span>Lokalizacja: {report.location || 'brak'}</span>
                            <span>Adres: {report.street_address || 'brak'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'issues' && (
            <div className="p-6">
              {loadingIssues ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2">Ładowanie zgłoszeń...</span>
                </div>
              ) : issues.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-4xl mb-4">📋</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Brak zgłoszeń
                  </h3>
                  <p className="text-sm">
                    Nie ma jeszcze zgłoszeń przypisanych do tego obiektu infrastruktury.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {issues.map((issue) => (
                    <div key={issue.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-sm font-medium text-gray-900">
                              {issue.id}
                            </h4>
                            <span className="px-2 py-1 text-xs font-medium rounded-full text-blue-600 bg-blue-50">
                              {issue.issue_type}
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              issue.status === 'zrealizowane' ? 'text-green-600 bg-green-50' :
                              issue.status === 'w_trakcie_realizacji' ? 'text-yellow-600 bg-yellow-50' :
                              issue.status === 'zaadresowane' ? 'text-blue-600 bg-blue-50' :
                              'text-gray-600 bg-gray-50'
                            }`}>
                              {issue.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-600">
                            <span>Zgłaszający: {issue.reporter_name}</span>
                            <span>Lokalizacja: {issue.location || 'brak'}</span>
                            <span>Adres: {issue.street_address || 'brak'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'requirements' && (
            <div className="p-6">
              {loadingRequirements ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2">Ładowanie wymagań...</span>
                </div>
              ) : requirements.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-4xl mb-4">📋</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Brak wymagań
                  </h3>
                  <p className="text-sm">
                    Nie ma jeszcze wymagań przypisanych do tego obiektu infrastruktury.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {requirements.map((req) => (
                    <div key={req.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-sm font-medium text-gray-900">
                              {req.subject}
                            </h4>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(req.priority)}`}>
                              {getPriorityLabel(req.priority)}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-600">
                            <span>Typ: {getTypeLabel(req.requirement_type)}</span>
                            <span>Data: {new Date(req.created_at).toLocaleDateString('pl-PL')}</span>
                            <span>Status: {req.status}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notatki dla obiektu infrastruktury
                </label>
                <textarea
                  rows={12}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Wpisz notatki dotyczące tego obiektu infrastruktury..."
                />
              </div>

              {isAdmin && (
                <div className="flex justify-end">
                  <button
                    onClick={saveNotes}
                    disabled={savingNotes}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-md font-medium"
                  >
                    {savingNotes ? 'Zapisywanie...' : 'Zapisz notatki'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  )
}
